import { mapStyles } from '@/utils/google-maps';
import { MEDIA_MIN_M } from '@/utils/mediaQuery';
import LocationIconRed from '@/assets/img/icon-location-outline.svg';
import LocationIcon from '@/assets/img/icon-location.svg';

export default {
  data: () => ({
    centerCoordinates: {
      lat: 51.1764483,
      lng: 10.6500592,
      zoom: 5.5,
    },
    infoWindowOptions: {
      pixelOffset: {
        width: 0,
        height: -40,
      },
    },
    mapStyles,
    loading: false,
    errors: null,
    storeList: [],
    storeListArray: [],
    userPosition: {
      lat: null,
      lng: null,
    },
    filterProperties: [],
  }),
  mounted() {
    this.centerCoordinates = this.defaultCenterCoordinates;
  },
  computed: {
    ...mapState(useMainStore, {
      baseStore: 'baseStore',
    }),

    defaultCenterCoordinates() {
      const config = useRuntimeConfig();

      if (this.baseStore === config.public.baseStoreAt) {
        return {
          lat: 47.603875,
          lng: 13.6474193,
          zoom: 8,
        };
      }

      if (this.baseStore === config.public.baseStorePl) {
        return {
          lat: 52.1961759,
          lng: 19.2362081,
          zoom: 7,
        };
      }

      if (this.baseStore === config.public.baseStoreFr) {
        return {
          lat: 47.1230715,
          lng: 3.1659101,
          zoom: 6.5,
        };
      }

      if (this.baseStore === config.public.baseStoreIt) {
        return {
          lat: 41.87194,
          lng: 12.56738,
          zoom: 6.5,
        };
      }

      if (this.baseStore === config.public.baseStoreLu) {
        return {
          lat: 49.815273,
          lng: 6.129582999999999,
          zoom: 6.5,
        };
      }

      if (this.baseStore === config.public.baseStoreBe) {
        return {
          lat: 50.49944444,
          lng: 4.47527778,
          zoom: 7,
        };
      }

      if (this.baseStore === config.public.baseStoreCh) {
        return {
          lat: 46.8,
          lng: 8.3,
          zoom: 6.8,
        };
      }

      if (this.baseStore === config.public.baseStoreIe) {
        return {
          lat: 52.865196,
          lng: -7.9794599,
          zoom: 7,
        };
      }

      if (this.baseStore === config.public.baseStoreDk) {
        return {
          lat: 55.497204,
          lng: 9.747172,
          zoom: 7,
        };
      }

      // BASESTORE_DE
      return {
        lat: 51.1764483,
        lng: 10.6500592,
        zoom: 5.5,
      };
    },

    hasStores() {
      return this.storeList && this.storeList.stores && this.storeList.stores.length > 0;
    },

    iconUrl() {
      return LocationIconRed;
    },

    userIconUrl() {
      return LocationIcon;
    },

    apiKey() {
      const runtimeConfig = useRuntimeConfig();
      const googleMapsApiKey = runtimeConfig.public.googleMapsApiKey;

      return googleMapsApiKey;
    },
  },
  methods: {
    ...mapActions(useMainStore, {
      getStoreList: 'getStoreList',
      getStoreListFiltered: 'getStoreListFiltered',
    }),

    ...mapActions(useDatalayerStore, {
      setStoreFinderAnalytics: 'setStoreFinderAnalytics',
    }),

    async handleSearch({ query, latitude, longitude, currentPage }) {
      try {
        this.loading = true;
        this.resetErrors();
        const { onlySalons, radius } = this;

        const storeList = await this.getStoreList({
          query,
          latitude,
          longitude,
          currentPage,
          onlySalons,
          radius,
        });

        if (currentPage) {
          this.storeList.stores = [...this.storeList.stores, ...storeList.stores];
          this.storeList.pagination = storeList.pagination;
        } else {
          this.storeList = storeList;
          this.storeListArray = this.storeList.stores;
        }

        if (this.storeListArray.length <= 0) {
          this.errors = {
            address: [this.$t('validation.noStoresFound')],
          };
        }

        if (this.storeListArray.length > 0 && storeList.sourceLatitude && storeList.sourceLongitude) {
          this.centerCoordinates.lat = onlySalons
            ? this.storeListArray[0].geoPoint?.latitude
            : storeList.sourceLatitude;
          this.centerCoordinates.lng = onlySalons
            ? this.storeListArray[0].geoPoint?.longitude
            : storeList.sourceLongitude;
          this.centerCoordinates.zoom = 10;
        }
      } catch (e) {
        this.errors = {
          address: [this.$t('validation.general')],
        };
      } finally {
        this.loading = false;
      }
    },

    async handleSearchFiltered({
      query,
      latitude = this.userPosition.lat,
      longitude = this.userPosition.lng,
      filterProperties,
      radius,
    } = {}) {
      try {
        this.loading = true;
        this.resetErrors();

        const storeList = await this.getStoreListFiltered({
          query,
          latitude,
          longitude,
          radius,
          filterProperties,
        });

        this.filterProperties = storeList.filterProperties;

        this.storeList = storeList;
        this.storeListArray = this.storeList.stores;

        if (this.storeListArray.length <= 0) {
          this.errors = {
            address: [this.$t('validation.noStoresFound')],
          };
        }

        if (this.storeListArray.length > 0 && storeList.sourceLatitude && storeList.sourceLongitude) {
          await this.setMapPosition(storeList.sourceLatitude, storeList.sourceLongitude);
        }

        await this.setStoreFinderAnalytics(storeList.analytics);
      } catch (e) {
        this.errors = {
          address: [this.$t('validation.general')],
        };
      } finally {
        this.loading = false;
      }
    },

    async handleLocateMe() {
      try {
        this.loading = true;

        await this.getCurrentUserPosition();

        await this.setMapPosition(this.userPosition.lat, this.userPosition.lng);

        await this.handleSearch({
          latitude: this.userPosition.lat,
          longitude: this.userPosition.lng,
        });
      } catch (e) {
        this.$log.error('Error in getting user position: ', e);
      } finally {
        this.loading = false;
      }
    },

    async handleLocateMeFiltered() {
      try {
        this.loading = true;

        await this.getCurrentUserPosition();

        await this.setMapPosition(this.userPosition.lat, this.userPosition.lng);

        await this.handleSearchFiltered();
      } catch (e) {
        this.$log.error('Error in getting user position: ', e);
      } finally {
        this.loading = false;
      }
    },

    getCurrentUserPosition() {
      return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
          reject(new Error('Geolocation is not supported by your browser.'));
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userPosition.lat = position.coords.latitude;
            this.userPosition.lng = position.coords.longitude;

            resolve(position.coords);
          },
          (error) => {
            reject(new Error(`Error getting user position: ${error.message}`));
          },
        );
      });
    },

    async setMapPosition(lat, lng, zoom = 10) {
      const map = await this.$refs.map.map;

      map.setCenter({ lat, lng });
      map.setZoom(zoom);

      if (window.matchMedia(MEDIA_MIN_M).matches) {
        map.panBy(150, 0);
      }
    },

    resetErrors() {
      this.errors = null;
    },
  },
};
